import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Clock, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SecurityHealthCheck from '../components/SecurityHealthCheck';
import SEO, { SITE, breadcrumb, orgRef } from '../components/SEO';

const HealthCheckPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20">
      <SEO
        path="/security-health-check"
        title="Free 60-Second Security Health Check | Kennyvicops Cyber Solutions Inc."
        description="Five practical questions to identify areas of your business worth reviewing. Get a personalized security snapshot delivered to your inbox — free, no credit card, 60 seconds."
        jsonLd={[
          breadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Security Health Check', path: '/security-health-check' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            url: `${SITE}/security-health-check`,
            name: 'Free 60-Second Security Health Check',
            about: orgRef,
            potentialAction: {
              '@type': 'AssessAction',
              name: 'Take the Security Health Check',
              target: `${SITE}/security-health-check`,
            },
          },
        ]}
      />

      {/* HERO — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(22,135,255,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-60" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-24 pb-14 sm:pb-20">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <span className="w-6 h-px bg-cyan-300/70" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Free Security Health Check</span>
          </div>
          <h1 className="font-editorial text-4xl sm:text-6xl lg:text-[4.25rem] font-semibold leading-[1.05] tracking-tight text-white">
            How healthy is your security posture?
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300/95 leading-relaxed max-w-3xl">
            Five practical questions. A short snapshot of where your business may be strong, where it may be worth reviewing, and practical next steps &mdash; delivered to your inbox.
          </p>

          <div className="mt-8 flex flex-wrap gap-4 sm:gap-6 text-sm text-slate-300">
            <div className="inline-flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-300" />
              <span>60 seconds</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <Mail className="w-4 h-4 text-cyan-300" />
              <span>Personalized email report</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-cyan-300" />
              <span>No credit card required</span>
            </div>
          </div>
        </div>
      </section>

      {/* QUIZ — off-white surface */}
      <section className="relative bg-[#F5F8FC] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <SecurityHealthCheck />

          <p className="mt-10 text-xs text-slate-500 max-w-2xl mx-auto text-center">
            This is a short snapshot, not a complete security assessment. It is designed to help
            you identify areas worth reviewing and start a practical conversation about your
            business. For a full assessment, please <Link to="/contact" className="text-blue-700 font-medium hover:underline">request a consultation</Link>.
          </p>
        </div>
      </section>

      {/* CTA — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(22,135,255,0.22),transparent_50%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-white leading-[1.1]">
            Ready to go deeper than a snapshot?
          </h2>
          <p className="mt-5 text-slate-300/95 text-base sm:text-lg max-w-2xl mx-auto">
            The Health Check is a starting point. A full security assessment identifies real risks in your environment and prioritizes practical improvements.
          </p>
          <div className="mt-8">
            <Link to="/contact">
              <Button
                data-testid="hc-page-cta"
                className="px-8 py-6 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold"
              >
                Request a Security Consultation <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthCheckPage;
